/**
 * MerchantBannerWizard — self-serve hero banner A/B wizard.
 *
 * Built to the 0.2 handoff (docs/design/design_handoff_merchant 0.2/banner-wizard):
 *
 *   detect (confirm the banner on both screens) → upload (two ratio-checked
 *         slots) → preview (in the store) → recap → running
 *
 * No intro screen: the flow opens on step 1. The Home entry card already
 * explains the mechanism, so a "here's what will happen" page was one click
 * between the merchant and the thing they came to do.
 *
 * ONE store window serves every screen ("la misma ventana abierta con el
 * viewport de celular"). We only ever REQUEST a screen (SHOW_SCREEN); the
 * overlay realizes it by resizing itself — the same mechanism the consultant
 * editor's "Switch to Mobile" uses (visual-editor/deviceViewport) — and answers
 * with the screen it actually ended up on, since an opener can neither resize
 * nor measure a cross-origin window. If the browser refused, we reopen at the
 * right size. Each breakpoint has its own banner element, so switching screens
 * re-detects (step 1) or re-adopts (preview).
 *
 * Talks to the store window over the TNX_MB_* protocol (merchantBannerProtocol),
 * origin-checked both ways. Launch creates the experiment + CTR custom events.
 */

import { useState, useEffect, useRef, useMemo, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { Card, Text, Button, Tag, ProgressBar, Spinner } from '@nimbus-ds/components'
import { UploadIcon } from '@nimbus-ds/icons'
import { useSetMerchantChrome } from '@/components/layout/MerchantChrome'
import { authService } from '@/api/auth'
import { storeService, resolveStorefront, type StoreInfo } from '@/api/store'
import { uploadStagingAsset, getStagingPreviewUrl } from '@/api/assets'
import { experimentService } from '@/api/experiments'
import { createCustomEvent } from '@/api/customEvents'
import { trackAppEvent } from '@/lib/appTelemetry'
import { fmtInt } from '@/lib/formatEsAr'
import {
  MB_MSG,
  MB_CMD,
  MB_POPUP_SIZE,
  deviceForViewport,
  buildSelfServeBannerConfig,
  bannerEventSelectors,
  type MbDevice,
  type MerchantBannerControl,
  type MerchantBannerUnsupportedReason,
  type MbUploads,
} from '@/lib/merchantBannerProtocol'
import { deriveUrlPatterns } from '@/lib/experimentScope'

type Step = 'detect' | 'upload' | 'preview' | 'recap' | 'done'

/**
 * Which family this run creates (OPTIMIZATIONS-SPEC.md §0):
 *   'experiment'   — A/B: one challenger, a verdict, a 30-day cap
 *   'optimization' — a slot: 2–3 challengers, always on, the split moves daily
 * Everything before launch (detect, upload, preview) is the same work; the mode changes how many
 * creatives are collected and what gets created at the end.
 */
export type WizardMode = 'experiment' | 'optimization'

/** Arm keys. A/B keeps the historical 'treatment'; slots use the multi-variant convention. */
const AB_ARM = 'treatment'
const SLOT_ARM_KEYS = ['variant_b', 'variant_c', 'variant_d']
/** 2 challengers by default; 3 is the ceiling the merchant can pick (control is always an arm). */
const DEFAULT_CHALLENGERS = 2
const MAX_CHALLENGERS = 3

/** Max experiment duration — the auto-stop backstop and the draw rule: if no
 *  winner emerges by the cap, the result is an honest "sin diferencia clara". */
const MAX_DURATION_DAYS = 30

/** Hard ceiling (blocks) vs the nudge we merely mention (RESPUESTA §5). */
const MAX_SIZE_KB = 1024
const NUDGE_SIZE_KB = 200
const ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp']
/** Ratio tolerance: ±12% still "fits" — themes crop a few px either way. */
const RATIO_TOLERANCE = 0.12

const DEVICES: MbDevice[] = ['desktop', 'mobile']
const DEVICE_LABEL: Record<MbDevice, string> = { desktop: 'Para computadora', mobile: 'Para celular' }
const DEVICE_EMOJI: Record<MbDevice, string> = { desktop: '🖥️', mobile: '📱' }
const DEVICE_WORD: Record<MbDevice, string> = { desktop: 'computadora', mobile: 'celular' }

interface UploadSlot {
  uploading?: boolean
  /** Blocking problem (format/weight/upload failure) — never a ratio mismatch. */
  error?: string
  /** Second line explaining how to fix a blocking problem. */
  errorDetail?: string
  asset_id?: string
  s3_key?: string
  previewUrl?: string
  fileName?: string
  width?: number
  height?: number
  sizeKB?: number
  /** Ratio differs from the detected banner; merchant may accept the crop. */
  ratioMismatch?: boolean
  forcedCrop?: boolean
}

/** Page headline per step (the shell top bar carries "Paso N de 4"). */
const STEP_TITLES: Record<Step, string> = {
  detect: 'Encontrá tu banner',
  upload: 'Subí el banner nuevo',
  preview: 'Miralo en tu tienda',
  recap: 'Listo para empezar',
  done: 'Prueba en marcha',
}

/** Slot variants of the step headlines — same work, different promise at the end. */
const SLOT_STEP_TITLES: Partial<Record<Step, string>> = {
  upload: 'Subí los banners nuevos',
  preview: 'Miralos en tu tienda',
  recap: 'Listo para activar',
  done: 'Optimización activa',
}

/**
 * Per-device blockers surfaced in Desk (handoff 3c). Carousels are NOT a
 * refusal — the overlay pins the confirmed slide and tests that one image.
 */
type DeviceIssue = 'closed_without_confirm' | 'wrong_page' | 'not_found'

const ISSUE_COPY: Record<DeviceIssue, { title: string; body: string; cta: string }> = {
  closed_without_confirm: {
    title: 'Cerraste la ventana sin elegir',
    body: 'No pasa nada — abrila de nuevo y marcá tu banner.',
    cta: 'Volver a abrir mi tienda',
  },
  wrong_page: {
    title: 'Estás navegando otra página',
    body: 'El banner que probamos es el de tu página de inicio. Volvé ahí para elegirlo.',
    cta: 'Ir a mi página de inicio',
  },
  not_found: {
    title: 'No encontramos un banner en tu inicio',
    body: 'Puede que tu página no tenga uno, o que esté armado de una forma que todavía no reconocemos. Tu consultor puede ayudarte.',
    cta: 'Volver a abrir mi tienda',
  },
}

/** Refusal reasons the overlay can still raise, mapped to the 3c states. */
const REASON_TO_ISSUE: Record<MerchantBannerUnsupportedReason, DeviceIssue | null> = {
  // Carousels stay supported (slide pinning) — never a refusal in Desk.
  carousel: null,
  not_found: 'not_found',
  wrong_page: 'wrong_page',
}

/** Read intrinsic pixel size of a picked file before uploading it. */
function readImageSize(file: File): Promise<{ width: number; height: number }> {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file)
    const img = new Image()
    img.onload = () => {
      URL.revokeObjectURL(url)
      resolve({ width: img.naturalWidth, height: img.naturalHeight })
    }
    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('No pudimos leer la imagen.'))
    }
    img.src = url
  })
}

/**
 * Thumbnail box in the banner's REAL proportions. A phone banner is usually
 * portrait, so it must not be stretched into the wide desktop frame — the
 * handoff gives mobile thumbs a fixed narrow width (140px) and lets desktop
 * ones fill, both at their own aspect ratio.
 */
function thumbStyle(device: MbDevice, w?: number, h?: number): React.CSSProperties {
  const aspectRatio = w && h ? `${w} / ${h}` : device === 'mobile' ? '4 / 5' : '16 / 5'
  return device === 'mobile' ? { aspectRatio, width: 140 } : { aspectRatio, width: '100%' }
}

const orientationWord = (w: number, h: number) =>
  w > h * 1.15 ? 'apaisada' : h > w * 1.15 ? 'vertical' : 'cuadrada'

// ---------------------------------------------------------------------------

export function MerchantBannerWizard({ mode = 'experiment' }: { mode?: WizardMode } = {}) {
  const storeId = authService.getCurrentStoreId()
  const uploadSessionId = useMemo(() => crypto.randomUUID(), [])
  /** Stable per-launch id, so an inserted challenger's data-tnx-insert-id can't collide with
   *  another slot's on the same store (§4.5). */
  const slotId = useMemo(() => uploadSessionId.slice(0, 8), [uploadSessionId])
  const isSlot = mode === 'optimization'

  const [step, setStep] = useState<Step>('detect')
  /** How many challengers the merchant is uploading (slots only; A/B is always one). */
  const [challengerCount, setChallengerCount] = useState(DEFAULT_CHALLENGERS)
  const arms = useMemo(
    () => (isSlot ? SLOT_ARM_KEYS.slice(0, challengerCount) : [AB_ARM]),
    [isSlot, challengerCount]
  )
  /** Which challenger the store window is currently previewing. */
  const [previewedArm, setPreviewedArm] = useState<string>(arms[0])
  const [storeUrl, setStoreUrl] = useState<string | null>(null)
  const [storeError, setStoreError] = useState<string | null>(null)
  const [openPopupDevice, setOpenPopupDevice] = useState<MbDevice | null>(null)
  /** Per-device detection status: which window is open and waiting on a confirm. */
  const [waitingDevice, setWaitingDevice] = useState<MbDevice | null>(null)
  const [issues, setIssues] = useState<Partial<Record<MbDevice, DeviceIssue>>>({})
  const [controls, setControls] = useState<Partial<Record<MbDevice, MerchantBannerControl>>>({})
  // Keyed by arm, then device: an A/B has one arm, a slot has two or three. Every arm keeps its own
  // pair of creatives because each screen is a different element on the storefront.
  const [uploads, setUploads] = useState<Record<string, Record<MbDevice, UploadSlot>>>(() =>
    Object.fromEntries([AB_ARM, ...SLOT_ARM_KEYS].map((arm) => [arm, { desktop: {}, mobile: {} }]))
  )
  const [previewedDevice, setPreviewedDevice] = useState<MbDevice | null>(null)

  // ONE store window, resized between screens ("la misma ventana abierta con el
  // viewport de celular"). The overlay resizes itself on our command — an opener
  // can't resize a cross-origin popup — and reports the viewport it ended up
  // with, which is also how we know if the browser refused.
  const popupRef = useRef<Window | null>(null)
  /** Which screen the store window says it is showing (only it can measure). */
  const storeScreenRef = useRef<MbDevice | null>(null)
  const activeDeviceRef = useRef<MbDevice>('desktop')
  const controlsRef = useRef(controls)
  controlsRef.current = controls
  // Preview actions arrive from the store window; the message listener stays
  // stable by calling through refs rather than depending on the handlers.
  const showVariantRef = useRef<((d: MbDevice) => void) | null>(null)
  const revertAllRef = useRef<(() => void) | null>(null)
  const openStoreRef = useRef<((d: MbDevice, o?: { redetect?: boolean; forceReopen?: boolean }) => void) | null>(null)
  const stepRefForPopup = useRef<Step>('detect')
  /** Guards the reopen-on-refused-resize path against looping. */
  const resizeFallbackRef = useRef(false)

  // Pilot instrumentation (B10).
  const stepRef = useRef<Step>(step)
  stepRef.current = step
  useEffect(() => {
    trackAppEvent('wizard_started')
    return () => {
      if (stepRef.current !== 'done') trackAppEvent('wizard_abandoned', { step: stepRef.current })
    }
  }, [])

  /**
   * Origins we accept messages from, and post commands to.
   *
   * A single derived origin is not safe: TiendaNube's `domain` field is the
   * *.mitiendanube.com host, and a store with a custom domain REDIRECTS there —
   * which changes the window's origin. postMessage then fails silently in both
   * directions (a mismatched targetOrigin is dropped; inbound fails the equality
   * check), so the wizard looks dead with no error anywhere. We therefore accept
   * any of the store's known hosts and latch whichever one actually talks to us.
   */
  const [allowedOrigins, setAllowedOrigins] = useState<string[]>([])
  const allowedKey = allowedOrigins.join('|')
  const activeOriginRef = useRef<string | null>(null)

  // --- Resolve the storefront URL and every origin it may end up on.
  useEffect(() => {
    let cancelled = false
    const resolve = async () => {
      const user = authService.getUserInfo() as any
      const tokenDomain = user?.store_url || user?.original_domain
      let info: StoreInfo | null = null
      if (storeId) {
        try {
          info = await storeService.getStore(storeId)
        } catch {
          /* fall back to whatever the token carried */
        }
      }
      if (cancelled) return
      const storefront = resolveStorefront(info, tokenDomain)
      if (!storefront) {
        setStoreError('No pudimos encontrar la dirección de tu tienda. Probá volver a iniciar sesión.')
        return
      }
      setStoreUrl(storefront.url)
      setAllowedOrigins(storefront.origins)
    }
    resolve()
    return () => {
      cancelled = true
    }
  }, [storeId])

  const sendToPopup = useCallback(
    (type: string, payload?: Record<string, unknown>) => {
      const win = popupRef.current
      if (!win || win.closed) return
      // Post to the origin the window actually talked to us from; before it has
      // said anything, fall back to the canonical one.
      const target = activeOriginRef.current || allowedOrigins[0]
      if (target) win.postMessage({ type, ...(payload || {}) }, target)
    },
    [allowedOrigins]
  )

  const pendingPreviewRef = useRef<MbDevice | null>(null)
  const uploadsRef = useRef(uploads)
  uploadsRef.current = uploads
  /** Which challenger the store window is showing — read from the message listener, which must
   *  stay stable, so it goes through a ref like every other cross-window handler here. */
  const previewedArmRef = useRef<string>(arms[0])

  // --- Overlay message listener (origin-checked against the store).
  useEffect(() => {
    if (allowedOrigins.length === 0) return
    const handler = (event: MessageEvent) => {
      // Accept any of the store's known hosts, then LATCH the one that spoke:
      // a redirect (mitiendanube → custom domain) changes the window's origin,
      // and everything we send afterwards has to target the new one.
      if (!allowedOrigins.includes(event.origin)) return
      activeOriginRef.current = event.origin
      const type = event.data?.type
      if (typeof type !== 'string' || !type.startsWith('TNX_MB_')) return
      switch (type) {
        case MB_MSG.READY: {
          const device = activeDeviceRef.current
          sendToPopup(MB_CMD.ACK)
          // Window reopened mid-flow: re-adopt that screen's confirmed control,
          // and replay a preview requested while it was closed.
          const known = controlsRef.current[device]
          if (known) {
            sendToPopup(MB_CMD.ADOPT, { selector: known.selector })
            if (pendingPreviewRef.current === device) {
              const url = uploadsRef.current[previewedArmRef.current]?.[device]?.previewUrl
              if (url) {
                sendToPopup(MB_CMD.PREVIEW, { previewUrl: url })
                sendToPopup(MB_CMD.PREVIEW_MODE, { device })
                setPreviewedDevice(device)
              }
              pendingPreviewRef.current = null
            }
          }
          break
        }
        case MB_MSG.SCREEN: {
          // The store window reports which screen it's actually showing. If it
          // couldn't honor the request, reopen at the right size — once, so a
          // refusal can't turn into an open/close loop.
          const showing = event.data?.device === 'mobile' ? 'mobile' : 'desktop'
          storeScreenRef.current = showing
          const want = activeDeviceRef.current
          if (showing !== want && !resizeFallbackRef.current) {
            resizeFallbackRef.current = true
            popupRef.current?.close()
            openStoreRef.current?.(want, { forceReopen: true })
          } else if (showing === want) {
            resizeFallbackRef.current = false
          }
          break
        }
        case MB_MSG.CANDIDATE: {
          // A candidate is on screen — clear any stale blocker for that device.
          const c = event.data?.control as MerchantBannerControl | undefined
          const device = deviceForViewport(c?.viewportWidth) ?? activeDeviceRef.current
          setIssues((prev) => ({ ...prev, [device]: undefined }))
          break
        }
        case MB_MSG.CONFIRMED: {
          const c = event.data?.control as MerchantBannerControl | undefined
          if (!c?.selector || !c?.elementType) break
          // Attribute by the popup's OWN viewport when it reports one, so a
          // confirmation in a window we didn't open last still lands right.
          const device = deviceForViewport(c.viewportWidth) ?? activeDeviceRef.current
          setControls((prev) => ({ ...prev, [device]: c }))
          setIssues((prev) => ({ ...prev, [device]: undefined }))
          setWaitingDevice((prev) => (prev === device ? null : prev))
          break
        }
        case MB_MSG.UNSUPPORTED: {
          const reason = event.data?.reason as MerchantBannerUnsupportedReason
          const issue = REASON_TO_ISSUE[reason] ?? 'not_found'
          if (!issue) break
          const device = activeDeviceRef.current
          setIssues((prev) => ({ ...prev, [device]: issue }))
          setWaitingDevice((prev) => (prev === device ? null : prev))
          break
        }
        // --- Preview step: the merchant decides inside the store window.
        case MB_MSG.PREVIEW_DEVICE: {
          const d = event.data?.device === 'mobile' ? 'mobile' : 'desktop'
          showVariantRef.current?.(d)
          break
        }
        case MB_MSG.PREVIEW_OK:
          revertAllRef.current?.()
          setStep('recap')
          break
        case MB_MSG.PREVIEW_BACK:
          revertAllRef.current?.()
          setStep('upload')
          break
      }
    }
    window.addEventListener('message', handler)
    return () => window.removeEventListener('message', handler)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowedKey, sendToPopup])

  // --- Popup lifecycle watchdog. Also turns "window closed while we were still
  // waiting for a confirmation" into the 3c blocker, instead of leaving the
  // card spinning forever on a window that no longer exists.
  useEffect(() => {
    const interval = setInterval(() => {
      const active = activeDeviceRef.current
      const win = popupRef.current
      const open = !!win && !win.closed
      setOpenPopupDevice(open ? active : null)
      if (!open) {
        storeScreenRef.current = null
        setWaitingDevice((waiting) => {
          if (!waiting) return waiting
          if (!controlsRef.current[waiting] && stepRefForPopup.current === 'detect') {
            setIssues((prev) => ({ ...prev, [waiting]: prev[waiting] || 'closed_without_confirm' }))
          }
          return null
        })
      }
    }, 800)
    return () => clearInterval(interval)
  }, [])

  /**
   * Show `device`'s screen in the store window: open it at that size, or resize
   * the one already open. `redetect` re-runs banner detection after the resize
   * (step 1 — a responsive theme serves a different element per breakpoint).
   */
  const openStore = useCallback(
    (device: MbDevice, opts?: { redetect?: boolean; forceReopen?: boolean }) => {
      if (!storeUrl) return
      activeDeviceRef.current = device
      const { width, height } = MB_POPUP_SIZE[device]
      const win = popupRef.current
      const alive = !!win && !win.closed

      if (alive && !opts?.forceReopen) {
        sendToPopup(MB_CMD.SHOW_SCREEN, { device, redetect: !!opts?.redetect })
        win!.focus()
      } else {
        const sep = storeUrl.includes('?') ? '&' : '?'
        const url =
          `${storeUrl}${sep}tnx_visual_editor=true&tnx_merchant_banner=1` +
          `&tnx_app_origin=${encodeURIComponent(window.location.origin)}`
        popupRef.current = window.open(
          url,
          'TNXMerchantBanner',
          `width=${width},height=${height}`
        )
      }

      storeScreenRef.current = null
      setOpenPopupDevice(device)
      setWaitingDevice(controlsRef.current[device] ? null : device)
      setIssues((prev) => ({ ...prev, [device]: undefined }))
    },
    [storeUrl, sendToPopup]
  )

  // --- Uploads: format + weight gate, then ratio check against that device's
  // detected banner. A ratio mismatch is NOT an error — it offers a crop escape.
  const handleFile = useCallback(
    async (arm: string, device: MbDevice, file: File) => {
      const setSlot = (patch: UploadSlot) =>
        setUploads((prev) => ({
          ...prev,
          [arm]: { ...prev[arm], [device]: { ...prev[arm][device], ...patch } },
        }))
      if (!ALLOWED_TYPES.includes(file.type)) {
        setSlot({ error: 'Formato no soportado. Usá JPG, PNG o WebP.' })
        return
      }
      const sizeKB = Math.round(file.size / 1024)
      if (sizeKB > MAX_SIZE_KB) {
        const mb = (file.size / (1024 * 1024)).toFixed(1).replace('.', ',')
        setSlot({
          error: `Esta imagen pesa ${mb} MB`,
          errorDetail:
            'El máximo es 1 MB — más que eso hace lenta tu tienda. Guardala con menos calidad o en WebP y volvé a subirla.',
        })
        return
      }
      if (!storeId) return
      setSlot({ uploading: true, error: undefined, ratioMismatch: false, forcedCrop: false })
      try {
        const [dims, result] = await Promise.all([
          readImageSize(file).catch(() => null),
          uploadStagingAsset(storeId, uploadSessionId, file, `${device}_image`),
        ])
        const control = controlsRef.current[device]
        let ratioMismatch = false
        if (dims && control?.width && control?.height) {
          const want = control.width / control.height
          const got = dims.width / dims.height
          ratioMismatch = Math.abs(got - want) / want > RATIO_TOLERANCE
        }
        const previewUrl = getStagingPreviewUrl(result.s3_key)
        setSlot({
          uploading: false,
          asset_id: result.asset_id,
          s3_key: result.s3_key,
          previewUrl,
          fileName: file.name,
          width: dims?.width,
          height: dims?.height,
          sizeKB,
          ratioMismatch,
          forcedCrop: false,
        })
        // Warm the image in the STORE window's cache (Chrome partitions HTTP
        // cache by top-level site — our thumbnail doesn't warm the popup's).
        sendToPopup(MB_CMD.WARM, { urls: [previewUrl] })
      } catch (err: any) {
        setSlot({ uploading: false, error: err?.message || 'No pudimos subir la imagen. Probá de nuevo.' })
      }
    },
    [storeId, uploadSessionId, sendToPopup]
  )

  /**
   * Put the challenger in front of the merchant on `device`. Each screen is its
   * own window (the mobile one must render under the 767px breakpoint), so
   * switching sides means opening/focusing that window — not resizing this one.
   */
  const showVariant = useCallback(
    (device: MbDevice, arm?: string) => {
      const targetArm = arm || previewedArmRef.current
      const url = uploadsRef.current[targetArm]?.[device]?.previewUrl
      if (!url) return
      activeDeviceRef.current = device
      previewedArmRef.current = targetArm
      setPreviewedArm(targetArm)
      const win = popupRef.current
      if (!win || win.closed) {
        // Reopen and queue: on READY we re-adopt the control and replay this.
        pendingPreviewRef.current = device
        openStore(device)
        return
      }
      // Same window, switched to this screen. The overlay sequences the change →
      // adopt this device's control → swap the image, because at the new
      // breakpoint the banner is a DIFFERENT element than the one on screen.
      sendToPopup(MB_CMD.SHOW_SCREEN, {
        device,
        redetect: false,
        adoptSelector: controlsRef.current[device]?.selector,
        previewUrl: url,
      })
      sendToPopup(MB_CMD.PREVIEW_MODE, { device })
      setPreviewedDevice(device)
      setOpenPopupDevice(device)
      win.focus()
    },
    [openStore, sendToPopup]
  )

  const revertAll = useCallback(() => {
    sendToPopup(MB_CMD.REVERT)
    setPreviewedDevice(null)
  }, [sendToPopup])

  showVariantRef.current = showVariant
  revertAllRef.current = revertAll
  openStoreRef.current = openStore

  // Step 3 happens in the store window: entering it puts the challenger up on
  // the desktop screen straight away, so Desk never shows a dead "click here"
  // card for something the merchant is meant to judge on their own site.
  const previewStartedRef = useRef(false)
  useEffect(() => {
    stepRefForPopup.current = step
    if (step !== 'preview') {
      previewStartedRef.current = false
      return
    }
    if (previewStartedRef.current) return
    previewStartedRef.current = true
    showVariant('desktop')
  }, [step, showVariant])

  // --- Slot resolution / gating -------------------------------------------
  const slotResolved = (arm: string, device: MbDevice) => {
    const s = uploads[arm]?.[device] || {}
    if (!s.asset_id || s.uploading) return false
    return s.ratioMismatch ? !!s.forcedCrop : true
  }
  const armResolved = (arm: string) => DEVICES.every((d) => slotResolved(arm, d))
  const bothDetected = !!controls.desktop && !!controls.mobile
  const allResolved = arms.every(armResolved)

  /** One variant config per challenger arm; null until every creative of every arm is in. */
  const assembledConfigs = useMemo(() => {
    if (!controls.desktop || !controls.mobile) return null
    const out: Record<string, ReturnType<typeof buildSelfServeBannerConfig>> = {}
    for (const arm of arms) {
      const mbUploads: MbUploads = {}
      for (const d of DEVICES) {
        const s = uploads[arm]?.[d]
        if (s?.asset_id && s.s3_key && s.previewUrl) {
          mbUploads[d] = { asset_id: s.asset_id, s3_key: s.s3_key, previewUrl: s.previewUrl }
        }
      }
      if (!mbUploads.desktop || !mbUploads.mobile) return null
      out[arm] = buildSelfServeBannerConfig(
        { desktop: controls.desktop, mobile: controls.mobile },
        mbUploads,
        { arm, slotId }
      )
    }
    return out
  }, [controls, uploads, arms, slotId])

  // --- Launch ---------------------------------------------------------------
  const [launching, setLaunching] = useState(false)
  const [launchError, setLaunchError] = useState<string | null>(null)

  const doLaunch = async () => {
    const cfgs = assembledConfigs
    if (!cfgs || !controls.desktop || !controls.mobile || !storeId || launching) return
    setLaunching(true)
    setLaunchError(null)
    try {
      // Unique per launch: event_id is evt-<name>-<store>, so a second banner
      // test on the same store must not collide with an active earlier one.
      const suffix = Date.now().toString(36)
      const clickEventName = `banner_test_click_${suffix}`
      const seenEventName = `banner_test_seen_${suffix}`

      // Both devices' banners count as "the banner", and on a bg-image theme so does every
      // INSERTED challenger wrapper — a selector list covers all of them (closest() and
      // querySelectorAll() both take lists). Without the inserted ids, a bg-image arm records zero
      // clicks no matter how good its creative is (OPTIMIZATIONS-SPEC.md §4.5).
      const selectors = bannerEventSelectors(
        { desktop: controls.desktop, mobile: controls.mobile },
        arms.map((arm) => ({ arm, slotId }))
      )

      // Where the test applies, read off the config instead of repeated as a literal here. The
      // template is homepage-only today, so this yields '/' — the same value this line used to
      // hardcode. It stops being the same value the moment a template applies somewhere else, and
      // a scope that disagrees with the config is invisible: too wide silently dilutes the
      // exposure denominator (every session counts as exposed), which is what made a Naturalú
      // banner test read ~18,700 weekly sessions instead of ~5,600.
      //
      // The fallback matters in the same direction. If a future template forgets to set urlPattern
      // on its entries, '/' scopes the test too NARROWLY and it reports no data — visible, and
      // caught immediately. Shipping '' instead would scope it site-wide and quietly wrong.
      const derivedScope =
        deriveUrlPatterns({
          elements_to_hide: arms.flatMap((a) => cfgs[a].elements_to_hide || []),
          elements_to_insert: arms.flatMap((a) => cfgs[a].elements_to_insert || []),
          elements_to_swap: arms.flatMap((a) => cfgs[a].elements_to_swap || []),
        }) || '/'

      const variants: Record<string, any> = {
        control: { name: 'Tu banner de hoy', description: 'Banner actual', config: {} },
      }
      const traffic_split: Record<string, number> = {}
      arms.forEach((arm, i) => {
        const cfg = cfgs[arm]
        variants[arm] = {
          name: isSlot ? `Banner ${i + 2}` : 'Banner nuevo',
          description: isSlot ? `Banner alternativo ${i + 1}` : 'Banner alternativo',
          config: {
            type: 'tier1_visual',
            modifications: [],
            captured_events: [],
            elements_to_hide: cfg.elements_to_hide,
            elements_to_insert: cfg.elements_to_insert,
            ...(cfg.elements_to_swap.length ? { elements_to_swap: cfg.elements_to_swap } : {}),
            // Self-serve marker: filters the merchant views and feeds the drift guard, which reads
            // the control image off whichever arm carries it.
            self_serve_banner: cfg.template_config.self_serve_banner,
          },
        }
      })
      // Even to start with. A slot's split is rewritten daily by the allocator; an A/B keeps 50/50
      // for its whole life.
      const armCount = arms.length + 1
      const each = Math.floor(100 / armCount)
      Object.keys(variants).forEach((key, i) => {
        traffic_split[key] = each + (i === 0 ? 100 - each * armCount : 0)
      })

      const payload: any = {
        name: isSlot
          ? `Banner principal — optimización (${new Date().toLocaleDateString('es-AR')})`
          : `Banner principal — prueba A/B (${new Date().toLocaleDateString('es-AR')})`,
        hypothesis: isSlot
          ? 'Mostrar más seguido el banner que más clics genera aumenta los clics totales.'
          : 'Un banner nuevo genera más clics que el actual.',
        objective: 'Aumentar los clics en el banner principal',
        type: 'tier1_visual',
        tier: 1,
        is_negative: false,
        mde: null,
        primary_segment: 'overall',
        // A/B stays Seguro-locked (SELF-SERVE-MVP-SPEC §1.1). A slot INHERITS the store's profile
        // instead — allocating is not deciding, and the posture drives ε, the step cap and the
        // freshness bars (OPTIMIZATIONS-SPEC §8). Omitting the field is what triggers inheritance
        // in experiments-handler (explicit → store default → balanced).
        ...(isSlot ? { mode: 'optimization' } : { risk_level: 'safe' }),
        hard_to_revert: false,
        traffic_split,
        targeting: {
          // COMMA-SEPARATED STRING, not an array — tnx-core and the bundle
          // template both .trim()/.split(',') this field; an array threw in
          // both and killed experiment application store-wide (aug-6 alarm).
          url_patterns: derivedScope,
          device_conditions: { desktop: true, mobile: true, tablet: true },
          exclusions: [],
        },
        variants,
        funnel_config: { use_default: true, selected_steps: [], custom_steps: [] },
        // Primary = banner clicks per session (BB+ROPE verdict runs on
        // goal_conversions/sessions). Visibility rides as secondary for the
        // exposure sanity check; purchases stay visible as guardrail.
        goals: {
          primary: { type: 'custom_event', event_name: clickEventName, label: 'Clic en el banner' },
          secondary: [
            { type: 'custom_event', event_name: seenEventName, label: 'Banner visto' },
            { type: 'purchase', label: 'Compra' },
          ],
        },
        // An A/B carries the 30-day auto-stop backstop. A slot carries no end date at all — it is
        // always-on by definition, and experiments-handler rejects one that tries to.
        ...(isSlot
          ? {}
          : { timing: { end_date_time: new Date(Date.now() + MAX_DURATION_DAYS * 86400_000).toISOString() } }),
      }

      const created = await experimentService.createExperiment(payload)

      // Events go DIRECT to /definitions rather than riding along as captured_events.
      //
      // The original reason — captured_events created inert drafts the tracker never loads — no
      // longer holds: experiments-handler now persists a COMPLETE capture as 'active'. What still
      // blocks the merge is that the captured shape cannot express these two events: the "seen"
      // event needs threshold 0.5 / duration 1000 (the capture path defaults to 0 and no
      // duration), and both need tags ['self_serve_banner'], which is what makes them show up in
      // the merchant views at all.
      //
      // Until the capture shape carries those, this stays four calls: create inactive → both
      // events → activate. The ordering below is the point of it.
      await createCustomEvent({
        store_id: storeId,
        event_name: clickEventName,
        display_name: isSlot ? 'Clic en banner (optimización)' : 'Clic en banner (prueba A/B)',
        category: 'interaction',
        tracking_config: {
          mode: 'auto',
          auto_config: {
            interaction_type: 'click',
            click_config: { selector: selectors, capture_text: false },
          },
        },
        experiment_id: created.id,
        tags: ['self_serve_banner'],
      })
      await createCustomEvent({
        store_id: storeId,
        event_name: seenEventName,
        display_name: isSlot ? 'Banner visto (optimización)' : 'Banner visto (prueba A/B)',
        category: 'engagement',
        tracking_config: {
          mode: 'auto',
          auto_config: {
            interaction_type: 'visibility',
            visibility_config: { selector: selectors, threshold: 0.5, duration_ms: 1000 },
          },
        },
        experiment_id: created.id,
        tags: ['self_serve_banner'],
      })

      // Activate only after both events exist — a failure above leaves an
      // inactive draft experiment, never a live test measuring nothing.
      await experimentService.updateExperiment(created.id, { status: 'active' } as any)
      trackAppEvent('launched', { experiment_id: created.id, mode })
      trackAppEvent('wizard_completed')
      revertAll()
      popupRef.current?.close()
      setStep('done')
    } catch (err: any) {
      setLaunchError(
        err?.response?.data?.error?.message ||
          err?.message ||
          (isSlot
            ? 'No pudimos activar la optimización. Probá de nuevo en unos minutos.'
            : 'No pudimos iniciar la prueba. Probá de nuevo en unos minutos.')
      )
    } finally {
      setLaunching(false)
    }
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  // The shell top bar carries the wizard's identity and step counter (0.2).
  const numbered: Step[] = ['detect', 'upload', 'preview', 'recap']
  const stepNumber = numbered.indexOf(step) + 1
  useSetMerchantChrome({
    title: isSlot ? 'Optimizá tu banner' : 'Probá otro banner',
    subtitle: stepNumber > 0 ? `Paso ${stepNumber} de ${numbered.length}` : undefined,
    // No way back from the confirmation — the test is already running.
    backTo: step === 'done' ? undefined : '/merchant',
    showStoreOnDesktop: true,
  })

  if (!storeId) {
    return (
      <Card>
        <Card.Body>
          <Text className="text-center">No pudimos identificar tu tienda. Volvé a iniciar sesión.</Text>
        </Card.Body>
      </Card>
    )
  }

  return (
    <div className="flex flex-col gap-5 md:gap-6">
      {step !== 'done' && (
        <h1 className="m-0 text-lg font-bold text-[var(--nimbus-colors-neutral-textHigh)] md:text-[1.375rem]">
          {isSlot && SLOT_STEP_TITLES[step] ? SLOT_STEP_TITLES[step] : STEP_TITLES[step]}
        </h1>
      )}

      {step === 'detect' && (
        <div className="flex flex-col gap-5">
          <Text>
            Abrimos tu tienda en una ventana nueva y te marcamos el banner que encontramos. Vos
            confirmás si es ese — una vez en computadora y otra en celular.
          </Text>
          <div className="grid gap-3 md:grid-cols-2 md:items-stretch md:gap-6">
            {DEVICES.map((device) => (
              <DetectSlotCard
                key={device}
                device={device}
                control={controls[device]}
                waiting={waitingDevice === device}
                issue={issues[device]}
                storeReady={!!storeUrl}
                // Switching screens re-runs detection: the mobile banner is a
                // different element than the desktop one.
                onOpen={() => openStore(device, { redetect: true })}
              />
            ))}
          </div>
          <Card>
            <Card.Body>
              <Text fontSize="caption">
                💡 Tu tienda muestra un banner distinto en cada pantalla, por eso lo elegís dos veces.
              </Text>
            </Card.Body>
          </Card>
          {storeError && <Text fontSize="caption">{storeError}</Text>}
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Text fontSize="caption">Todavía no cambia nada en tu tienda.</Text>
            <Button appearance="primary" disabled={!bothDetected} onClick={() => setStep('upload')}>
              Continuar
            </Button>
          </div>
        </div>
      )}

      {step === 'upload' && (
        <div className="flex flex-col gap-5">
          <Text>
            {isSlot
              ? 'Cada banner necesita su versión de computadora y de celular. Usamos tus banners actuales como referencia de tamaño.'
              : 'Tu tienda muestra un banner distinto en computadora y en celular. Subí los dos; usamos tus banners actuales como referencia de tamaño.'}
          </Text>

          {isSlot && (
            <Card>
              <Card.Body>
                <div className="flex flex-wrap items-center gap-3">
                  <Text fontSize="caption" fontWeight="medium">¿Cuántos banners nuevos querés probar?</Text>
                  {Array.from({ length: MAX_CHALLENGERS - 1 }, (_, i) => i + DEFAULT_CHALLENGERS).map((n) => (
                    <button
                      key={n}
                      onClick={() => setChallengerCount(n)}
                      className={`rounded-full border px-3 py-1 text-xs ${
                        challengerCount === n
                          ? 'border-[#0059d5] bg-[#e7f0ff] text-[#0059d5]'
                          : 'border-[var(--nimbus-colors-neutral-interactive)] text-[var(--nimbus-colors-neutral-textLow)]'
                      }`}
                    >
                      {n} banners
                    </button>
                  ))}
                  <Text fontSize="caption">Más tu banner de hoy, que siempre entra en la rotación.</Text>
                </div>
              </Card.Body>
            </Card>
          )}

          {arms.map((arm, armIndex) => (
            <div key={arm} className="flex flex-col gap-3">
              {isSlot && (
                <Text fontWeight="medium" color="neutral-textHigh">
                  Banner {armIndex + 2}
                </Text>
              )}
              <div className="grid gap-5 md:grid-cols-2 md:items-stretch">
                {DEVICES.map((device) => (
                  <UploadSlotCard
                    key={device}
                    device={device}
                    control={controls[device]!}
                    slot={uploads[arm][device]}
                    onFile={(f) => handleFile(arm, device, f)}
                    onReset={() =>
                      setUploads((p) => ({ ...p, [arm]: { ...p[arm], [device]: {} } }))
                    }
                    onForceCrop={() =>
                      setUploads((p) => ({
                        ...p,
                        [arm]: { ...p[arm], [device]: { ...p[arm][device], forcedCrop: true } },
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          ))}

          <Card>
            <Card.Body>
              <Text fontSize="caption">
                💡 ¿Tenés una sola imagen? Podés usar la misma para los dos, pero en celular se va a
                recortar a lo alto.
              </Text>
            </Card.Body>
          </Card>
          <div className="flex flex-wrap items-center justify-end gap-3">
            <Text fontSize="caption">
              {isSlot
                ? 'Al principio los mostramos por igual, hasta aprender cuál funciona mejor.'
                : 'Desde que empieza, la mitad de tus visitas ve cada banner.'}
            </Text>
            <Button
              appearance="primary"
              disabled={!allResolved}
              onClick={() => {
                const urls = arms
                  .flatMap((a) => DEVICES.map((d) => uploads[a][d].previewUrl))
                  .filter((u): u is string => !!u)
                if (urls.length) sendToPopup(MB_CMD.WARM, { urls })
                setStep('preview')
              }}
            >
              Continuar
            </Button>
          </div>
        </div>
      )}

      {/* Step 3 happens in the store window — Desk just waits. The merchant
          switches screens and accepts from the floating card over their site. */}
      {step === 'preview' && (
        <Card>
          <Card.Body>
            {openPopupDevice ? (
              <div className="flex flex-col gap-3">
                <div className="flex items-center gap-2">
                  <Spinner size="small" />
                  <Text fontWeight="medium" color="neutral-textHigh">Mirando tu tienda</Text>
                </div>
                <Text>
                  {isSlot
                    ? 'Abrimos tu tienda con uno de los banners nuevos puesto. Decinos ahí si se ve bien, y mirá los demás desde acá.'
                    : 'Abrimos tu tienda con el banner nuevo puesto. Decinos ahí si se ve bien.'}
                </Text>
                {isSlot && (
                  <div className="flex flex-wrap items-center gap-2">
                    {arms.map((arm, i) => (
                      <button
                        key={arm}
                        onClick={() => showVariant(previewedDevice || 'desktop', arm)}
                        className={`rounded-full border px-3 py-1 text-xs ${
                          previewedArm === arm
                            ? 'border-[#0059d5] bg-[#e7f0ff] text-[#0059d5]'
                            : 'border-[var(--nimbus-colors-neutral-interactive)] text-[var(--nimbus-colors-neutral-textLow)]'
                        }`}
                      >
                        Ver banner {i + 2}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="flex flex-col gap-2">
                <Text fontWeight="medium" color="neutral-textHigh">
                  La ventana de tu tienda está cerrada
                </Text>
                <Text>Tu banner nuevo sigue guardado. Abrila de nuevo para verlo antes de empezar.</Text>
                <button
                  className="self-start text-xs text-[#0059d5] hover:text-[#00429f]"
                  onClick={() => showVariant(previewedDevice || 'desktop')}
                >
                  Volver a abrir mi tienda
                </button>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      {step === 'recap' && controls.desktop && controls.mobile && (
        <div className="flex flex-col gap-4">
          <Text>
            {isSlot
              ? 'Desde ahora rotamos entre estos banners y, a medida que aprendemos, mostramos más seguido el que más clics recibe. No termina: se ajusta solo.'
              : 'Desde ahora, la mitad de tus visitas ve cada banner. Cuando haya una respuesta clara, te avisamos.'}
          </Text>
          <Card>
            <Card.Body>
              <div className="flex flex-col gap-3">
                {DEVICES.map((device) => (
                  <div key={device} className="flex flex-col gap-1.5">
                    <Text fontSize="caption" fontWeight="medium">
                      {DEVICE_EMOJI[device]} {DEVICE_LABEL[device]}
                    </Text>
                    <div
                      className="grid gap-3"
                      style={{ gridTemplateColumns: `repeat(${arms.length + 1}, minmax(0, 1fr))` }}
                    >
                      <ComparisonCell label="Tu banner de hoy" src={controls[device]!.imageUrl} />
                      {arms.map((arm, i) => (
                        <ComparisonCell
                          key={arm}
                          label={`Banner ${i + 2}${
                            uploads[arm][device].fileName ? ` · ${uploads[arm][device].fileName}` : ''
                          }`}
                          src={uploads[arm][device].previewUrl || null}
                        />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card.Body>
          </Card>
          <Text fontSize="caption">
            {isSlot
              ? 'Podés frenarla, dejar uno fijo o sacar un banner cuando quieras. Tu tienda no cambia de diseño: solo alterna estas imágenes. Si el ganador empieza a gastarse, te avisamos.'
              : `Podés frenar la prueba cuando quieras. Tu tienda no cambia de diseño: solo alterna estas dos imágenes. La prueba corre sola y se detiene automáticamente (máximo ${MAX_DURATION_DAYS} días). Además miramos tus ventas: un banner que junta clics pero vende menos no va a ganar.`}
          </Text>
          {launchError && <Text fontSize="caption">{launchError}</Text>}
          <Button appearance="primary" onClick={doLaunch} disabled={launching || !assembledConfigs}>
            {launching
              ? isSlot ? 'Activando…' : 'Iniciando…'
              : isSlot ? 'Activar la optimización' : 'Empezar la prueba'}
          </Button>
        </div>
      )}

      {step === 'done' && (
        <div className="flex flex-col items-center gap-5 px-4 py-8 text-center">
          <span className="text-[44px] leading-none">{isSlot ? '🎛️' : '🧪'}</span>
          <div className="flex max-w-[300px] flex-col gap-2">
            <Text fontWeight="bold" fontSize="highlight" color="neutral-textHigh">
              {isSlot ? 'Tu optimización ya está activa' : 'Tu prueba ya está corriendo'}
            </Text>
            <Text>
              {isSlot
                ? 'No tenés que hacer nada más. Por ahora los mostramos por igual; en unos días vas a ver acá cuál se lleva más clics.'
                : 'No tenés que hacer nada más. En unos días vas a ver acá cuál banner genera más clics.'}
            </Text>
          </div>
          <Card>
            <Card.Body>
              <div className="flex w-full flex-col gap-1.5 text-left">
                <Text fontSize="caption">Progreso</Text>
                <ProgressBar value={2} appearance="primary" />
                <Text fontSize="caption">Recién empieza — todavía no participaron visitas.</Text>
              </div>
            </Card.Body>
          </Card>
          <Text fontSize="caption">
            {isSlot
              ? 'Si cambiás tu banner desde el administrador, no pasa nada: lo empezamos a medir de nuevo desde ese día.'
              : 'Importante: no cambies el banner desde el administrador de tu tienda mientras la prueba está activa.'}
          </Text>
          <div className="flex w-full max-w-[320px] flex-col gap-2.5">
            <Link to="/merchant" className="no-underline">
              <Button appearance="primary">Volver al resumen</Button>
            </Link>
            <Link
              to={isSlot ? '/merchant/banner-optimization/results' : '/merchant/banner-test/results'}
              className="no-underline"
            >
              <Button appearance="transparent">{isSlot ? 'Ver la optimización' : 'Ver la prueba'}</Button>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------

/**
 * Image that falls back to the placeholder box when the URL doesn't load.
 * A store thumbnail can fail for reasons we can't see from here (hotlink
 * protection, an expired CDN URL) — better an honest "no preview" than a
 * blank rectangle the merchant reads as a broken app.
 */
function Thumb({
  src,
  alt,
  className,
  style,
  fallback,
}: {
  src: string | null
  alt: string
  className?: string
  style?: React.CSSProperties
  fallback: React.ReactNode
}) {
  const [failed, setFailed] = useState(false)
  useEffect(() => setFailed(false), [src])
  if (!src || failed) return <>{fallback}</>
  return <img src={src} alt={alt} className={className} style={style} onError={() => setFailed(true)} />
}

/**
 * One device's detection state (handoff 3a/3b/3c): Confirmado / Esperando /
 * Pendiente, plus the blockers that need the merchant to go back to the window.
 * Carousels are confirmable — the dimensions line just says which image we test.
 */
function DetectSlotCard({
  device,
  control,
  waiting,
  issue,
  storeReady,
  onOpen,
}: {
  device: MbDevice
  control?: MerchantBannerControl
  waiting: boolean
  issue?: DeviceIssue
  storeReady: boolean
  onOpen: () => void
}) {
  const tag = control
    ? { appearance: 'success' as const, label: 'Confirmado' }
    : waiting
      ? { appearance: 'primary' as const, label: 'Esperando' }
      : { appearance: 'neutral' as const, label: 'Pendiente' }
  const openLabel = device === 'mobile' ? 'Abrir mi tienda en celular' : 'Abrir mi tienda'

  return (
    <Card>
      <Card.Body>
        <div className="flex h-full flex-col gap-3.5">
          <div className="flex items-center gap-2">
            <Text fontWeight="medium" color="neutral-textHigh">
              {DEVICE_EMOJI[device]} En {DEVICE_WORD[device]}
            </Text>
            <div className="ml-auto shrink-0 whitespace-nowrap">
              <Tag appearance={tag.appearance}>{tag.label}</Tag>
            </div>
          </div>

          {control ? (
            <>
              <Thumb
                src={control.imageUrl}
                alt={`Tu banner actual en ${DEVICE_WORD[device]}`}
                className="rounded-md border border-[#e7e7e7] object-cover"
                style={thumbStyle(device, control.width, control.height)}
                fallback={
                  <div
                    className="flex items-center justify-center rounded-md border border-[#e7e7e7] bg-[#fafafa] p-2 text-center text-xs text-[#888]"
                    style={thumbStyle(device, control.width, control.height)}
                  >
                    Vista previa no disponible — está marcado en tu tienda
                  </div>
                }
              />
              <div className="mt-auto flex items-center justify-between gap-3 pt-1">
                <Text fontSize="caption">
                  <span className="tabular-nums">{control.width} × {control.height}</span>
                  {control.isCarousel && ' · carrusel, probamos esta imagen'}
                </Text>
                <button className="text-xs text-[#0059d5] hover:text-[#00429f]" onClick={onOpen}>
                  Elegir otro
                </button>
              </div>
            </>
          ) : issue ? (
            <div
              className={`flex flex-col gap-1 rounded-md px-3.5 py-3 ${
                issue === 'not_found' ? 'border border-[#f3d1d1] bg-[#fdf3f3]' : 'bg-[#fafafa]'
              }`}
            >
              <span
                className={`text-[.8125rem] font-medium ${
                  issue === 'not_found' ? 'text-[#b91c1c]' : 'text-[var(--nimbus-colors-neutral-textHigh)]'
                }`}
              >
                {ISSUE_COPY[issue].title}
              </span>
              <Text fontSize="caption">{ISSUE_COPY[issue].body}</Text>
              <button
                className="self-start text-xs text-[#0059d5] hover:text-[#00429f]"
                onClick={onOpen}
              >
                {ISSUE_COPY[issue].cta}
              </button>
            </div>
          ) : waiting ? (
            <div className="flex flex-col gap-2">
              <div className="flex items-center gap-2">
                <Spinner size="small" />
                <Text fontSize="caption">Confirmá tu banner en la ventana que abrimos.</Text>
              </div>
              <button
                className="self-start text-xs text-[#0059d5] hover:text-[#00429f]"
                onClick={onOpen}
              >
                Volver a abrir mi tienda
              </button>
            </div>
          ) : (
            <>
              {/* The dotted silhouette carries this screen's shape — it's the
                  cue that mobile is a different, taller banner. */}
              <div className="hidden items-center gap-3 rounded-md border-[1.5px] border-dashed border-[#b8b8b8] bg-[#fafafa] p-3 md:flex">
                <span
                  className="shrink-0 rounded border border-dashed border-[#c8c8c8]"
                  style={{ width: 70, aspectRatio: device === 'mobile' ? '4/5' : '16/5' }}
                />
                <Text fontSize="caption">
                  {device === 'mobile'
                    ? 'Abrimos tu tienda como se ve en un celular para que marques el banner de esa pantalla.'
                    : 'Abrimos tu tienda como la ve alguien desde una computadora.'}
                </Text>
              </div>
              <Button appearance="neutral" onClick={onOpen} disabled={!storeReady}>
                {openLabel}
              </Button>
            </>
          )}
        </div>
      </Card.Body>
    </Card>
  )
}

/**
 * A vs B cell. Fixed HEIGHT with automatic width (handoff 1b: "miniatura 80px
 * de alto"), so a portrait phone banner and a wide desktop one each keep their
 * own shape instead of being squeezed into a common box.
 */
function ComparisonCell({ label, src }: { label: string; src: string | null }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Text fontSize="caption">{label}</Text>
      <Thumb
        src={src}
        alt={label}
        className="h-20 w-auto self-start rounded-md border border-[#e7e7e7] object-contain"
        fallback={
          <div className="flex h-20 w-full items-center justify-center rounded-md border border-[#e7e7e7] bg-[#fafafa] text-[10px] text-[#888]">
            Sin vista previa
          </div>
        }
      />
    </div>
  )
}

function UploadSlotCard({
  device,
  control,
  slot,
  onFile,
  onReset,
  onForceCrop,
}: {
  device: MbDevice
  control: MerchantBannerControl
  slot: UploadSlot
  onFile: (f: File) => void
  onReset: () => void
  onForceCrop: () => void
}) {
  const box = thumbStyle(device, control.width, control.height)
  const resolved = !!slot.asset_id && (!slot.ratioMismatch || slot.forcedCrop)
  const needsReview = !!slot.ratioMismatch && !slot.forcedCrop

  return (
    <Card>
      <Card.Body>
        <div className="flex h-full flex-col gap-3.5">
          <div className="flex items-center gap-2">
            <Text fontWeight="bold" color="neutral-textHigh">
              {DEVICE_EMOJI[device]} {DEVICE_LABEL[device]}
            </Text>
            <div className="ml-auto shrink-0 whitespace-nowrap">
              {needsReview ? (
                <Tag appearance="danger">Revisar</Tag>
              ) : resolved ? (
                <Tag appearance="success">Listo</Tag>
              ) : (
                <Tag appearance="neutral">Pendiente</Tag>
              )}
            </div>
          </div>

          {/* Detected control, as the size reference. */}
          <div className="flex flex-col gap-1.5">
            <Text fontSize="caption">Tu banner de hoy · {control.width} × {control.height}</Text>
            <Thumb
              src={control.imageUrl}
              alt={`Banner actual en ${DEVICE_WORD[device]}`}
              className="rounded-md border border-[#e7e7e7] object-cover"
              style={box}
              fallback={
                <div
                  className="flex items-center justify-center rounded-md bg-gradient-to-br from-[#d8d8d8] to-[#c2c2c2] text-xs text-[#767676]"
                  style={box}
                >
                  Banner actual
                </div>
              }
            />
          </div>

          {/* The new one. */}
          <div className="flex flex-col gap-1.5">
            <Text fontSize="caption">El nuevo{slot.fileName ? ` · ${slot.fileName}` : ''}</Text>
            {slot.previewUrl ? (
              <div className="flex flex-col gap-2">
                <img
                  src={slot.previewUrl}
                  alt={`Banner nuevo para ${DEVICE_WORD[device]}`}
                  className="rounded-md border border-[#e7e7e7] object-cover"
                  style={box}
                />
                {needsReview ? (
                  <div className="flex flex-col gap-1 rounded-md border border-[#f3d1d1] bg-[#fdf3f3] px-3.5 py-3">
                    <span className="text-[.8125rem] font-medium text-[#b91c1c]">
                      {slot.fileName} es {orientationWord(slot.width || 0, slot.height || 0)} (
                      {slot.width} × {slot.height})
                    </span>
                    <Text fontSize="caption">
                      En {DEVICE_WORD[device]} tu banner es{' '}
                      {orientationWord(control.width, control.height)} ({control.width} ×{' '}
                      {control.height}) — se recortaría. Subí una versión pensada para{' '}
                      {DEVICE_WORD[device]}.
                    </Text>
                    <div className="flex items-center gap-3">
                      <button className="text-xs text-[#0059d5] hover:text-[#00429f]" onClick={onForceCrop}>
                        Usarla igual (recortada)
                      </button>
                      <button className="text-xs text-[#0059d5] hover:text-[#00429f]" onClick={onReset}>
                        Elegir otra imagen
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs text-[#00935b]">
                      ✓ {slot.width} × {slot.height} · {fmtInt(slot.sizeKB || 0)} KB —{' '}
                      {slot.forcedCrop ? 'la vamos a recortar' : 'perfecta'}
                    </span>
                    <button className="text-xs text-[#0059d5] hover:text-[#00429f]" onClick={onReset}>
                      Cambiar
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <label
                className={`flex cursor-pointer flex-col items-center gap-2 rounded-lg border-[1.5px] border-dashed px-4 py-[18px] text-center ${
                  slot.error ? 'border-[#f3d1d1] bg-[#fdf3f3]' : 'border-[#b8b8b8] bg-white'
                }`}
              >
                <input
                  type="file"
                  accept={ALLOWED_TYPES.join(',')}
                  className="hidden"
                  disabled={slot.uploading}
                  onChange={(e) => {
                    const f = e.target.files?.[0]
                    if (f) onFile(f)
                    e.target.value = ''
                  }}
                />
                {/* The dotted frame carries the EXPECTED ratio — it's the main
                    signal of what shape of image this slot needs. */}
                <span
                  className="flex w-[72px] items-center justify-center rounded border border-dashed border-[#c8c8c8]"
                  style={{ aspectRatio: box.aspectRatio }}
                >
                  <UploadIcon size={20} />
                </span>
                <span className="text-sm font-medium text-[#0059d5]">
                  {slot.uploading ? 'Subiendo…' : `Subir imagen para ${DEVICE_WORD[device]}`}
                </span>
                {slot.error ? (
                  <span className="flex flex-col gap-1">
                    <span className="text-[.8125rem] font-medium text-[#b91c1c]">{slot.error}</span>
                    {slot.errorDetail && (
                      <span className="text-xs leading-normal text-[#5d5d5d]">{slot.errorDetail}</span>
                    )}
                    <span className="text-xs text-[#0059d5]">Elegir otra imagen</span>
                  </span>
                ) : (
                  <span className="text-xs leading-normal text-[#888]">
                    Igual que tu banner actual:{' '}
                    <strong className="text-[var(--nimbus-colors-neutral-textLow)]">
                      {control.width} × {control.height}
                    </strong>
                  </span>
                )}
              </label>
            )}
          </div>

          <Text fontSize="caption">
            JPG, PNG o WebP · menos de {NUDGE_SIZE_KB} KB carga más rápido.
          </Text>
        </div>
      </Card.Body>
    </Card>
  )
}
